import React, { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from './auth-context'
import type { 
  LifeDomain, 
  CreateLifeDomainInput, 
  UpdateLifeDomainInput,
  Label,
  CreateLabelInput,
  UpdateLabelInput,
} from '@/types'

interface LifeDomainsContextType {
  lifeDomains: LifeDomain[]
  labels: Label[]
  loading: boolean
  createDomain: (domain: CreateLifeDomainInput) => Promise<LifeDomain>
  updateDomain: (id: string, updates: UpdateLifeDomainInput) => Promise<LifeDomain>
  deleteDomain: (id: string) => Promise<void>
  createLabel: (label: CreateLabelInput) => Promise<Label>
  updateLabel: (id: string, updates: UpdateLabelInput) => Promise<Label>
  deleteLabel: (id: string) => Promise<void>
  getLabelsForDomain: (domainId: string) => Label[]
  getDefaultLabel: (domainId: string) => Label | undefined
  refetch: () => Promise<void>
}

const LifeDomainsContext = createContext<LifeDomainsContextType | undefined>(undefined)

export function LifeDomainsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [lifeDomains, setLifeDomains] = useState<LifeDomain[]>([])
  const [labels, setLabels] = useState<Label[]>([])
  const [loading, setLoading] = useState(false)
  const domainIdsRef = useRef(new Set<string>())

  useEffect(() => {
    domainIdsRef.current = new Set(lifeDomains.map(d => d.id))
  }, [lifeDomains])

  const fetchLifeDomains = useCallback(async () => {
    if (!user?.id) {
      setLifeDomains([])
      setLabels([])
      return
    }

    setLoading(true)
    try {
      const { data: domainData, error: domainError } = await supabase
        .from('life_domains')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })

      if (domainError) {
        console.error('Failed to fetch life domains:', domainError)
        throw domainError
      }

      const domains = domainData || []
      setLifeDomains(domains)

      // Fetch labels for all domains
      if (domains.length > 0) {
        const domainIds = domains.map(d => d.id)
        const { data: labelData, error: labelError } = await supabase
          .from('life_domains_labels')
          .select('*')
          .in('life_domain_id', domainIds)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: true })

        if (labelError) {
          console.error('Failed to fetch labels:', labelError)
        } else {
          setLabels(labelData || [])
        }
      } else {
        setLabels([])
      }
    } catch (error) {
      console.error('Error fetching life domains:', error)
      setLifeDomains([])
      setLabels([])
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetchLifeDomains()
  }, [fetchLifeDomains])

  const createDomain = useCallback(async (domain: CreateLifeDomainInput): Promise<LifeDomain> => {
    if (!user?.id) {
      throw new Error('User must be authenticated to create a life domain')
    }

    try {
      const { data, error } = await supabase
        .from('life_domains')
        .insert({
          user_id: user.id,
          title: domain.title,
          description: domain.description || null,
          color: domain.color || '#3b82f6',
          sort_order: domain.sort_order || 0,
        })
        .select()
        .single()

      if (error) {
        console.error('Failed to create life domain:', error)
        throw error
      }

      setLifeDomains(prev => [...prev, data])
      domainIdsRef.current.add(data.id)

      // The database trigger auto-creates a default label; refetch labels
      try {
        await fetchLabelsForDomains([...domainIdsRef.current])
      } catch (labelError) {
        console.error('Failed to fetch labels after domain creation:', labelError)
      }

      return data
    } catch (error) {
      console.error('Error creating life domain:', error)
      throw error
    }
  }, [user?.id])

  const updateDomain = useCallback(async (id: string, updates: UpdateLifeDomainInput): Promise<LifeDomain> => {
    if (!user?.id) {
      throw new Error('User must be authenticated to update a life domain')
    }

    const previousDomains = [...lifeDomains]
    const domainToUpdate = lifeDomains.find(d => d.id === id)

    if (!domainToUpdate) {
      throw new Error(`Life domain with id ${id} not found`)
    }

    const optimisticDomain: LifeDomain = {
      ...domainToUpdate,
      ...updates,
      updated_at: new Date().toISOString(),
    }

    setLifeDomains(prev => 
      prev.map(d => d.id === id ? optimisticDomain : d)
    )

    try {
      const { data, error } = await supabase
        .from('life_domains')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) {
        console.error('Failed to update life domain:', error)
        setLifeDomains(previousDomains)
        throw error
      }

      setLifeDomains(prev => 
        prev.map(d => d.id === id ? data : d)
      )

      // DB trigger syncs default label color; refetch labels to stay in sync
      if (updates.color !== undefined) {
        try {
          await fetchLabelsForDomains(lifeDomains.map(d => d.id))
        } catch (labelError) {
          console.error('Failed to fetch labels after domain update:', labelError)
        }
      }

      return data
    } catch (error) {
      console.error('Error updating life domain:', error)
      setLifeDomains(previousDomains)
      throw error
    }
  }, [user?.id, lifeDomains])

  const deleteDomain = useCallback(async (id: string): Promise<void> => {
    if (!user?.id) {
      throw new Error('User must be authenticated to delete a life domain')
    }

    const previousDomains = [...lifeDomains]
    const previousLabels = [...labels]

    setLifeDomains(prev => prev.filter(d => d.id !== id))
    setLabels(prev => prev.filter(l => l.life_domain_id !== id))

    try {
      const { error } = await supabase
        .from('life_domains')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)

      if (error) {
        console.error('Failed to delete life domain:', error)
        setLifeDomains(previousDomains)
        setLabels(previousLabels)
        throw error
      }
    } catch (error) {
      console.error('Error deleting life domain:', error)
      setLifeDomains(previousDomains)
      setLabels(previousLabels)
      throw error
    }
  }, [user?.id, lifeDomains, labels])

  // Helper to refetch labels for a set of domain IDs
  const fetchLabelsForDomains = useCallback(async (domainIds: string[]) => {
    if (domainIds.length === 0) {
      setLabels([])
      return
    }

    const { data: labelData, error: labelError } = await supabase
      .from('life_domains_labels')
      .select('*')
      .in('life_domain_id', domainIds)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: true })

    if (labelError) {
      console.error('Failed to fetch labels:', labelError)
      throw labelError
    }
    setLabels(labelData || [])
  }, [])

  const createLabel = useCallback(async (labelInput: CreateLabelInput): Promise<Label> => {
    try {
      const { data, error } = await supabase
        .from('life_domains_labels')
        .insert({
          life_domain_id: labelInput.life_domain_id,
          name: labelInput.name,
          description: labelInput.description || null,
          priority: labelInput.priority || 'No priority',
          events_type: labelInput.events_type || null,
          intentional_living: labelInput.intentional_living || false,
          color: labelInput.color || '#3b82f6',
        })
        .select()
        .single()

      if (error) {
        console.error('Failed to create label:', error)
        throw error
      }

      setLabels(prev => [...prev, data])
      return data
    } catch (error) {
      console.error('Error creating label:', error)
      throw error
    }
  }, [])

  const updateLabel = useCallback(async (id: string, updates: UpdateLabelInput): Promise<Label> => {
    const previousLabels = [...labels]
    const labelToUpdate = labels.find(l => l.id === id)

    if (!labelToUpdate) {
      throw new Error(`Label with id ${id} not found`)
    }

    const optimisticLabel: Label = {
      ...labelToUpdate,
      ...updates,
      updated_at: new Date().toISOString(),
    }

    setLabels(prev => prev.map(l => l.id === id ? optimisticLabel : l))

    try {
      const { data, error } = await supabase
        .from('life_domains_labels')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('Failed to update label:', error)
        setLabels(previousLabels)
        throw error
      }

      setLabels(prev => prev.map(l => l.id === id ? data : l))
      return data
    } catch (error) {
      console.error('Error updating label:', error)
      setLabels(previousLabels)
      throw error
    }
  }, [labels])

  const deleteLabel = useCallback(async (id: string): Promise<void> => {
    const previousLabels = [...labels]

    setLabels(prev => prev.filter(l => l.id !== id))

    try {
      const { error } = await supabase
        .from('life_domains_labels')
        .delete()
        .eq('id', id)

      if (error) {
        console.error('Failed to delete label:', error)
        setLabels(previousLabels)
        throw error
      }
    } catch (error) {
      console.error('Error deleting label:', error)
      setLabels(previousLabels)
      throw error
    }
  }, [labels])

  const getLabelsForDomain = useCallback((domainId: string): Label[] => {
    return labels.filter(l => l.life_domain_id === domainId)
  }, [labels])

  const getDefaultLabel = useCallback((domainId: string): Label | undefined => {
    return labels.find(l => l.life_domain_id === domainId && l.is_default)
  }, [labels])

  const refetch = useCallback(async () => {
    await fetchLifeDomains()
  }, [fetchLifeDomains])

  const value = useMemo(() => ({
    lifeDomains,
    labels,
    loading,
    createDomain,
    updateDomain,
    deleteDomain,
    createLabel,
    updateLabel,
    deleteLabel,
    getLabelsForDomain,
    getDefaultLabel,
    refetch,
  }), [
    lifeDomains,
    labels,
    loading,
    createDomain,
    updateDomain,
    deleteDomain,
    createLabel,
    updateLabel,
    deleteLabel,
    getLabelsForDomain,
    getDefaultLabel,
    refetch,
  ])

  return (
    <LifeDomainsContext.Provider value={value}>
      {children}
    </LifeDomainsContext.Provider>
  )
}

export function useLifeDomains() {
  const context = useContext(LifeDomainsContext)
  if (context === undefined) {
    throw new Error('useLifeDomains must be used within a LifeDomainsProvider')
  }
  return context
}
